---
title: "Margin, Leverage and Liquidation Price on {{BRAND}}"
slug: margin-leverage-liquidation-price
shortTitle: "Margin & Liquidation Price"
description: "How leverage, margin required and margin level work on {{BRAND}}, what happens at stop-out under cross margin, and how to estimate your own liquidation price."
level: intermediate
category: trading
course: platform-guide
order: 5
topics: [leverage-margin, risk-management]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/margin-leverage-liquidation-price.svg
faqs:
  - q: "Why doesn't {{BRAND}} show a liquidation price?"
    a: "Under cross margin, every position shares the same account equity, so the price at which stop-out happens depends on your balance and all your positions together, and it shifts as they move. The platform shows Margin Level and Free Margin instead, and you can estimate a stop-out price from those figures."
  - q: "Does lower leverage make a trade safer?"
    a: "Leverage alone does not change how much you gain or lose for each $1 the price moves; position size does. Lower leverage mainly limits how large a position your free margin allows. For a position of a given size, safety comes from its size compared with your equity, and from your stop loss."
  - q: "What happens at the stop-out level?"
    a: "When your margin level reaches {{STOP_OUT_LEVEL}}, the platform closes the position with the largest loss first. It then checks again and closes positions one at a time until the margin level recovers."
  - q: "Can I use isolated margin on {{BRAND}}?"
    a: "No. {{BRAND}} uses cross margin only, so your whole account equity backs every open position."
  - q: "Do pending orders affect my margin?"
    a: "Yes. A Limit or Stop order reserves margin as soon as it is placed, which reduces your free margin before the order has filled."
---

**Leverage** lets you open a position worth more than the money you put up. **Margin** is the part of your money set aside to hold that position. On {{BRAND}} you see both on screen: the **Leverage** button and **Margin required** in the order panel, and **Margin Level** in the client bar.

What you will not see is a **liquidation price**, the price at which your position would be closed automatically. This guide explains each number, the alert and stop-out rules, and how to estimate that price yourself, with every step worked through in USDT.

<Warning title="Leverage magnifies losses as well as gains">

Profit and loss are calculated on the full position value, not on your margin, so a small price move can wipe out a large share of the money behind a trade. Positions can be closed automatically at a loss. Most traders lose money trading leveraged CFDs; only trade money you can afford to lose.

</Warning>

<CTA />

<KeyTakeaways>

- **Margin required = position value ÷ leverage.** You choose leverage per order with the Adjust Leverage slider, up to {{MAX_LEVERAGE}}.
- **Cross margin pools everything.** Your whole account equity backs every position; there is no isolated-margin mode.
- **Margin level drives stop-out.** Margin level is equity ÷ used margin. Alerts come at the warning and critical levels, and at the stop-out level the largest loss is closed first.
- **You can estimate your own stop-out price.** The equity you have above the stop-out threshold, divided by Value of $1 move, tells you how far the price can move against you.

</KeyTakeaways>

## Choosing leverage

<Steps>
<Step title="Open the leverage control">

In the order panel, press **Leverage: Nx**. The **Adjust Leverage** slider opens.

</Step>
<Step title="Pick a level">

Drag the slider. It goes up to {{MAX_LEVERAGE}}, and your account may have a lower cap.

</Step>
<Step title="Watch the summary change">

**Margin required** and **Free margin after** update as you move the slider. **Value of $1 move** does not, because it depends only on your size.

</Step>
</Steps>

<Screenshot name="leverage-slider" alt="The Adjust Leverage slider opened from the Leverage button in the order panel" caption="Leverage is chosen per order with the Adjust Leverage slider." />

## Margin required: what leverage changes and what it doesn't

<Example title="One position, three leverage settings (illustrative prices)">

You buy 0.1 BTC at 60,000. The position value is 0.1 × 60,000 = **6,000 USDT**.

- At 5x: margin required 6,000 ÷ 5 = **1,200 USDT**.
- At 10x: 6,000 ÷ 10 = **600 USDT**.
- At 20x: 6,000 ÷ 20 = **300 USDT**.

In all three, Value of $1 move is **0.1 USDT**, so a 1,000-dollar fall costs 1,000 × 0.1 = **100 USDT**.

</Example>

Leverage changes how much margin is locked. It does not change what each price move is worth; your size does that. The real danger of high leverage is that it lets a small account open a large position.

## Cross margin: one pool behind every position

{{BRAND}} uses **cross margin** only. All your equity stands behind all your positions. A profit on one trade can support a loss on another, but a large loss on one trade can also lead to others being closed. There is no isolated mode that ring-fences a single position. [Isolated vs Cross Margin](/academy/article/isolated-vs-cross-margin) compares the two models.

Hedging is allowed: you can hold a buy and a sell in the same market at once. For the matched volume, the panel shows **Margin required (hedged)**.

## Margin level, alerts and stop-out

The client bar shows **Equity**, **Open P/L**, **Balance**, **Free Margin**, **Used Margin** and **Margin Level**, where:

- **Equity** = balance + credit + unrealized profit or loss, including swap.
- **Margin Level** = equity ÷ used margin, shown as a percentage.

<CompareTable caption="What happens as your margin level falls">

| Margin level | What happens |
| --- | --- |
| Well above the warning level | Normal trading |
| At {{MARGIN_WARNING_LEVEL}} | Warning alert |
| At {{MARGIN_CRITICAL_LEVEL}} | Critical alert |
| At {{STOP_OUT_LEVEL}} | Stop-out: the position with the largest loss is closed, then the next, one at a time, until the level recovers |

</CompareTable>

<Screenshot name="client-bar-margin-level" alt="The client bar showing Equity, Open P/L, Balance, Free Margin, Used Margin and Margin Level" caption="The client bar: Margin Level is equity ÷ used margin." />

{/* TODO: verify with platform — how margin alerts are delivered (in-app, email, push) and that stop-out closures show as Liquidation in the Reason column. */}

<Example title="Stop-out with two positions (illustrative prices)">

Your balance is 2,000 USDT. You hold 0.1 BTC bought at 60,000 at 10x (600 USDT margin) and 2 ounces of gold bought at 2,400 at 20x (4,800 ÷ 20 = 240 USDT margin). Used margin is 600 + 240 = 840 USDT. Say the stop-out level were 50%.

- Bitcoin falls to 44,000: (44,000 − 60,000) × 0.1 = −1,600 USDT. Gold slips to 2,380: (2,380 − 2,400) × 2 = −40 USDT.
- Equity: 2,000 − 1,600 − 40 = 360 USDT. Margin level: 360 ÷ 840 = **about 43%**, below 50%.
- Stop-out closes the Bitcoin position first, because it has the largest loss. The balance becomes 2,000 − 1,600 = 400 USDT, and only the gold's 240 USDT of margin remains in use.
- New margin level: (400 − 40) ÷ 240 = **150%**. That is back above 50%, so the gold position stays open.

</Example>

Here the trade that caused the problem is the one closed, but under cross margin a big enough loss on one position can also put others at risk.

## Estimating your stop-out price

There is no liquidation price on screen because, under cross margin, it depends on everything in your account at once. With one position, or one dominant position, you can estimate it from numbers you can see:

<Steps>
<Step title="Find the stop-out threshold">

Multiply **Used Margin** by the stop-out level. That is the equity at which stop-out begins.

</Step>
<Step title="Work out your room">

Subtract that threshold from your current **Equity**. This is how much you can lose before stop-out.

</Step>
<Step title="Turn room into a price distance">

Divide the room by the position's **Value of $1 move**, shown in the order summary when you opened it. For markets priced in USDT it equals the units you hold, such as 0.1 BTC or 2 ounces of gold.

</Step>
<Step title="Apply it to the closing price">

For a buy, subtract the distance from the current Bid. For a sell, add it to the current Ask.

</Step>
</Steps>

<Example title="Estimating a stop-out price (illustrative prices)">

Your balance is 1,000 USDT and you buy 0.1 BTC at 60,000 at 10x, using 600 USDT of margin. Say the stop-out level were 50% (a hypothetical figure; check your own account's level).

- Threshold: 50% × 600 = **300 USDT** of equity.
- Room: 1,000 − 300 = **700 USDT**.
- Distance: 700 ÷ 0.1 = **7,000**.
- Estimated stop-out price: 60,000 − 7,000 = **about 53,000** on the Bid.

</Example>

<CompareTable caption="The same balance, different positions (50% stop-out assumed, illustrative prices)">

| Position | Margin used | Room | Estimated stop-out |
| --- | --- | --- | --- |
| 0.1 BTC at 5x | 1,200 | 400 | about 56,000 |
| 0.1 BTC at 10x | 600 | 700 | about 53,000 |
| 0.1 BTC at 20x | 300 | 850 | about 51,500 |
| 0.05 BTC at 10x | 300 | 850 | about 43,000 |

</CompareTable>

Notice the last row. Halving the position moves the estimate far more than any leverage setting, because each 1,000-dollar move now costs only 50 USDT. Size, compared with your equity, is what drives your risk.

Treat the result as an estimate. It ignores the spread, fees and swap, and with several positions the largest loss is closed first, which changes the picture. Fast markets and gaps can also jump straight past it.

{/* TODO: verify with platform — whether a position's margin stays fixed at entry or is recalculated as the price moves. */}

## Keeping a safe buffer

- **Put your stop loss well before your stop-out price.** In the example above, a stop at 57,500 would close the trade long before 53,000.
- **Keep free margin.** Every new position and pending order reduces it.
- **Don't add to losing trades** to "average down"; under cross margin it pulls the whole account closer to stop-out.
- **Recheck after every new trade,** because each one moves the stop-out price of the others.

<ProTip>

After opening a trade, run the four steps above and write the estimate in your journal next to your stop loss. If the two prices are close, the position is too large for your account.

</ProTip>

{{BRAND}} writes off a negative cash balance once no positions are open, but you should never rely on that as a plan.

{/* TODO: verify with platform — negative balance protection wording. */}

For the general theory, see [Leverage and Margin Explained Simply](/academy/article/leverage-and-margin) and [Liquidation and Margin Calls](/academy/article/liquidation-and-margin-calls).

## Conclusion

On {{BRAND}}, leverage sets how much margin a position locks, margin level measures your equity against that margin, and when it reaches {{STOP_OUT_LEVEL}}, stop-out closes the largest losing position first. Because margin is shared, no liquidation price is shown, but you can estimate one from Equity, Used Margin and Value of $1 move. Keep positions small relative to your equity and your stop loss well clear of that estimate.
